import { Model, type ModelProps } from '@/infra/model'
import { once } from 'lodash-es'
import { action, computed, observable } from 'mobx'
import { generateAvatarFallback } from '@/utils/image-util'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import { Id } from '@/infra/id'
import { unixEpoch } from '@/utils/date-util'
import type { LegendServiceType } from './legend-service-type'

/**
 * Legend version ID.
 */
export interface LegendVersionId {
  readonly id: string
  readonly version: number
}

/**
 * Creates a Legend Version ID.
 *
 * @param id
 * @param version
 */
export function LegendVersionId(id: string, version: number): LegendVersionId {
  return {
    id,
    version,
  }
}

/**
 * Whether 2 Legend Version IDs are considered the same.
 */
LegendVersionId.equals = function equals(
  left: LegendVersionId,
  right: LegendVersionId,
) {
  return (
    left === right || (left.id === right.id && left.version === right.version)
  )
}

/**
 * Asserts the Legend Version ID is non-empty.
 */
LegendVersionId.assert = function assert(
  input: LegendVersionId | undefined | null,
): LegendVersionId {
  if (
    LegendVersionId.equals(input ?? LegendVersionId.none, LegendVersionId.none)
  ) {
    throw new TypeError(
      'A non-empty Legend Version ID was expected, but was empty.',
    )
  }
  return input!
}

/**
 * Formats the Legend Version ID as a string.
 *
 * @param legendVersionId
 */
LegendVersionId.asString = function asString(legendVersionId: LegendVersionId) {
  return `${legendVersionId.id}:${legendVersionId.version}`
}

/**
 * No ID.
 */
LegendVersionId.none = {
  id: 'none',
  version: -1,
} as LegendVersionId

/**
 * A template of a Legend.
 */
export interface LegendTemplate {
  id: string
  name: string
}

export interface PublishedLegendProps extends ModelProps<PublishedLegend> {
  imageUrl?: string
}

/**
 * A Legend that has been Published and is ready to be used for new jobs.
 * Contains only props used for Job-related purposes.
 */
export class PublishedLegend extends Model<PublishedLegend> {
  @observable private accessor _imageUrl: string | undefined = undefined

  id = Id.none
  @observable accessor version = -1
  @observable accessor name = 'Legend'
  @observable accessor serviceType: LegendServiceType = 'Tax'
  @observable accessor description = ''
  @observable accessor workspace = Workspace.none
  @observable accessor viewOrder = 1000
  @observable accessor dateCreated = unixEpoch()
  @observable accessor inactive = false

  // The following props are only available on a full Legend.
  @observable accessor allowScheduling = false
  @observable accessor allowProSelection = false
  @observable accessor skipBilling = false
  @observable accessor skipQuote = false
  @observable accessor showAvailabilityCount = false
  @observable accessor priceUnit = ''
  @observable accessor templates: LegendTemplate[] = []

  /**
   * Empty instance.
   */
  static get none() {
    return lazyNone()
  }

  /**
   * Legend version ID.
   */
  @computed
  get versionId(): LegendVersionId {
    if (!this.id) {
      return LegendVersionId.none
    }

    return {
      id: Id.assert(this.id),
      version: this.version,
    }
  }

  /**
   * Image URL to use if any.
   */
  @computed
  get imageUrl() {
    return (
      this._imageUrl ||
      generateAvatarFallback(this.name, this.workspace.branding.colors.primary)
    )
  }

  @action.bound
  override set({ imageUrl, ...props }: Partial<PublishedLegendProps>): this {
    if (imageUrl) {
      this._imageUrl = imageUrl
    }

    return super.set(props)
  }
}

const lazyNone = once(() => new PublishedLegend().set({ name: 'Legend' }))
